import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api, imageUrl } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { ProductImagePlaceholder } from "./Logo";

export default function SearchOverlay() {
  const { searchOpen, setSearchOpen } = useShop();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!searchOpen) { setQuery(""); setResults([]); return; }
    const t = setTimeout(async () => {
      if (!query.trim()) { setResults([]); return; }
      setLoading(true);
      try {
        const { data } = await api.get("/products", { params: { search: query } });
        setResults(data);
      } catch { setResults([]); }
      setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [query, searchOpen]);

  return (
    <AnimatePresence>
      {searchOpen && (
        <motion.div
          className="fixed inset-0 z-[90] bg-[#080808]/97 backdrop-blur-xl"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="search-overlay"
        >
          <div className="max-w-3xl mx-auto px-6 pt-24">
            <div className="flex items-center gap-4 border-b border-[#D4AF37]/30 pb-4">
              <Search size={22} className="text-[#D4AF37]" strokeWidth={1.5} />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search fragrances by name..."
                className="flex-1 bg-transparent font-serif-display text-2xl sm:text-3xl text-white placeholder:text-[#666666] focus:outline-none"
                data-testid="search-input"
              />
              <button onClick={() => setSearchOpen(false)} aria-label="Close search" data-testid="search-close-button"
                className="text-white/80 hover:text-[#D4AF37] transition-colors">
                <X size={24} strokeWidth={1.5} />
              </button>
            </div>
            <div className="mt-8 space-y-3 max-h-[60vh] overflow-y-auto">
              {loading && <p className="font-sans-body text-sm text-[#B8B8B8]">Searching...</p>}
              {!loading && query && results.length === 0 && (
                <p className="font-sans-body text-sm text-[#B8B8B8]" data-testid="search-no-results">
                  No fragrances found for "{query}".
                </p>
              )}
              {results.map((p) => (
                <button
                  key={p.id}
                  onClick={() => { setSearchOpen(false); navigate(`/product/${p.id}`); }}
                  className="w-full flex items-center gap-4 p-3 gold-hairline bg-[#0D0D0D]/60 hover:border-[#D4AF37]/60 transition-colors text-left"
                  data-testid={`search-result-${p.id}`}
                >
                  <div className="w-12 h-14 shrink-0 overflow-hidden">
                    {p.image_url ? (
                      <img src={imageUrl(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <ProductImagePlaceholder name={p.name} className="w-full h-full" />
                    )}
                  </div>
                  <div>
                    <p className="font-serif-display text-lg text-white">{p.name}</p>
                    <p className="font-sans-body text-[11px] uppercase tracking-[0.2em] text-[#C6A15B]">{p.category}</p>
                  </div>
                  <span className="ml-auto font-mono-price text-[#D4AF37] text-sm">
                    ${Math.min(...p.sizes.map((s) => s.price)).toFixed(2)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
